import type { TooltipModel } from "chart.js";
import { forwardRef, type CSSProperties, type ReactNode, useMemo } from "react";

import { cn } from "../../lib/cn";

const TOOLTIP_HORIZONTAL_OFFSET = 16;
const TOOLTIP_PADDING = 8;
const TOOLTIP_ESTIMATED_HEIGHT = 150;
const TOOLTIP_ESTIMATED_WIDTH = 220;

interface ChartTooltipBoxProps {
  tooltip: TooltipModel<any>;
  interactive?: boolean;
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
}

const ChartTooltipBox = forwardRef<HTMLDivElement, ChartTooltipBoxProps>(
  function ChartTooltipBox(
    { tooltip, interactive = false, children, className, style },
    ref,
  ) {
    const position = useMemo(() => {
      const canvas = tooltip.chart.canvas;
      // Chart.js clears the canvas on destroy while React may retain the tooltip.
      if (!canvas) {
        return null;
      }

      const canvasLeft = canvas.offsetLeft;
      const canvasTop = canvas.offsetTop;
      const canvasRight = canvasLeft + canvas.clientWidth;
      const canvasBottom = canvasTop + canvas.clientHeight;
      const anchorX = canvasLeft + tooltip.caretX;
      const anchorY = canvasTop + tooltip.caretY;
      const rightSideLeft = anchorX + TOOLTIP_HORIZONTAL_OFFSET;
      const leftSideLeft =
        anchorX - TOOLTIP_HORIZONTAL_OFFSET - TOOLTIP_ESTIMATED_WIDTH;
      const left =
        rightSideLeft + TOOLTIP_ESTIMATED_WIDTH <= canvasRight
          ? rightSideLeft
          : Math.max(canvasLeft + TOOLTIP_PADDING, leftSideLeft);
      const top = Math.min(
        Math.max(
          anchorY - TOOLTIP_ESTIMATED_HEIGHT / 2,
          canvasTop + TOOLTIP_PADDING,
        ),
        canvasBottom - TOOLTIP_ESTIMATED_HEIGHT - TOOLTIP_PADDING,
      );

      return { left, top };
    }, [tooltip.caretX, tooltip.caretY, tooltip.chart.canvas]);

    if (!position) {
      return null;
    }

    return (
      <div
        ref={ref}
        className={cn(
          "bg-background border-border absolute z-50 w-[220px] rounded-lg border px-3 py-2.5 text-xs opacity-90 shadow-[0_8px_24px_rgba(17,16,14,0.12)] transition-[left,top,opacity] duration-150",
          className,
        )}
        style={{
          ...position,
          ...style,
          pointerEvents: interactive ? "auto" : "none",
          userSelect: interactive ? "auto" : "none",
        }}
      >
        {children}
      </div>
    );
  },
);

export { ChartTooltipBox };
